import React, { useState, useEffect, useMemo } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import styled from '@emotion/styled'
import { getCookie } from 'cookies-next'
import { useSelector } from 'react-redux'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import { getFrontCart, postKtcPoiByid3 } from 'api'
import withAuth from 'hoc/withAuth'
import Header from 'components/Header'
import { DEFAULT_LANG } from 'constants/lang'
import CartCardKTC from 'components/CartSection/CartCardKTC'
import KTCPromotionsColumn from 'components/ktc/KTCPromotionsColumn'
import Space from 'components/common/Space'
import KTCHorizen from 'components/ktc/KTCHorizen'
import MemberJoin from 'components/ktc/MemberJoin'
import { promotionRate } from 'lib'
import Image from 'next/image'
import ModalStore from 'components/StoreSection/ModalStore'
import ModalProduct from 'components/ProductSection/ModalProduct'

const Cart = ({ initialData, initialKtcData }) => {
  const router = useRouter()
  const token = useSelector((state) => state.auth.token)
  const {
    t,
    i18n: { language: lang },
  } = useTranslation()

  const [data, setData] = useState(initialData.data ?? [])
  const [showModalStore, setShowModalStore] = useState(false)
  const [showModalProduct, setShowModalProduct] = useState(false)
  const category = initialData.store?.category ?? 'food'

  // 현재 장바구니에 담겨있는 상품의 가격 총합
  const [totalPrice, setTotalPrice] = useState(() => {
    let price = 0

    data.map((item) => {
      // 상품 가격
      price += parseInt(item.price) * item.quantity
      // 옵션 가격
      item.options.length > 0 &&
        item.options.map(
          (option) => (price += parseInt(option.price) * item.quantity)
        )
    })

    return price
  })

  // 현재 장바구니에 담겨있는 총 상품의 갯수
  const [totalQuantity, setTotalQuantity] = useState(() => {
    let quantity = 0
    data.map((item) => (quantity += item.quantity))
    return quantity
  })

  const calculateKtcRate = () => {
    if (initialKtcData.error) return

    let arr = []
    const promotions_2 = Object.values(initialKtcData.promotions_2)
    promotions_2.map((item) => item.map((promotion) => arr.push(promotion)))

    return arr.filter(
      (item) => item.name === 'KOREA TRAVEL CARD' || item.name == '韩游一卡通'
    )[0].rate
  }

  // 캐시백 금액 계산
  const cashback = useMemo(() => {
    if (initialKtcData.error) return 0
    return promotionRate(totalPrice, calculateKtcRate())
  }, [initialKtcData])

  const [didMount, setDidMount] = useState(false)

  useEffect(() => {
    if (!didMount) return

    if (!token) return
    getFrontCart({ token, s_lang: lang }).then((res) => setData(res.data))
  }, [lang])

  useEffect(() => {
    setDidMount(true)
  }, [])

  return (
    <>
      <Header title={t('cart')} />
      {data.length > 0 ? (
        <>
          <Container>
            <ul>
              {data.map((item) => (
                <CartCardKTC
                  key={item.cart_id}
                  product={item}
                  data={data}
                  setData={setData}
                  totalPrice={totalPrice}
                  setTotalPrice={setTotalPrice}
                  totalQuantity={totalQuantity}
                  setTotalQuantity={setTotalQuantity}
                  category={category}
                />
              ))}
            </ul>
            <Link
              href={
                category === 'tour'
                  ? `/tour/${data[0].store_id}`
                  : `/food/${data[0].store_id}`
              }
              className="link"
              style={{ color: '#333' }}
            >
              {t('cart:other_product_add')}
            </Link>

            <Space top={32} />
            {showModalStore && (
              <ModalStore data={initialKtcData} onClose={setShowModalStore} />
            )}
            {!initialKtcData.error && (
              <Image
                src="/ktc/info.png"
                alt=""
                width={24}
                height={24}
                style={{
                  display: 'block',
                  marginLeft: 'auto',
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
                onClick={() => setShowModalStore(true)}
              />
            )}
            <KTCPromotionsColumn
              promotions_2={initialKtcData.promotions_2}
              price={totalPrice}
              styles={{
                backgroundColor: '#f9f9f9',
                borderRadius: 8,
              }}
            />

            <KTCHorizen />

            <MemberJoin cashback={cashback} />

            <Space bottom={32} />

            <div className="space-between">
              <p className="immediately">{t('ktc:immediately_payment')}</p>
              {showModalProduct && (
                <ModalProduct onClose={setShowModalProduct} />
              )}
              <Image
                src="/ktc/info.png"
                alt=""
                width={24}
                height={24}
                style={{ cursor: 'pointer' }}
                onClick={() => setShowModalProduct(true)}
              />
            </div>
          </Container>

          {initialData.data.length > 0 && (
            <Footer>
              <button
                onClick={() => {
                  if (category === 'tour') router.push('/order/form/tour')
                  else router.push('/order/form')
                }}
              >
                {t('cart:payment').replace('N', totalQuantity)} (
                {parseInt(totalPrice).toLocaleString('ko-kr')}{' '}
                {data[0].currency})
              </button>
            </Footer>
          )}
        </>
      ) : (
        <Container style={{ marginTop: 72 }}>
          {t('cart:empty')
            .split('</h3>')
            .map((item, index) => (
              <h3 key={index}>{item}</h3>
            ))}
          <span className="material-icons-outlined icon">
            production_quantity_limits
          </span>
        </Container>
      )}
    </>
  )
}

export default withAuth(Cart)

export const getServerSideProps = async (ctx) => {
  const initialLang =
    ctx.locale === 'zh-cn' ? 'zh-Hans' : getCookie('lang', ctx) || DEFAULT_LANG
  const token = getCookie('token', ctx)
  const initialData = await getFrontCart(
    { token, s_lang: initialLang },
    'server'
  )

  const store_id =
    initialData.status === 'success' && initialData.data.length > 0
      ? initialData.store.id
      : null

  const initialKtcData = await postKtcPoiByid3(
    { store_id, lang: initialLang },
    'server'
  )

  return {
    props: {
      initialData,
      initialKtcData,
      ...(await serverSideTranslations(ctx.locale, [
        'common',
        'cart',
        'order',
        'ktc',
      ])),
    },
  }
}

const Container = styled.main`
  padding-bottom: 140px;

  a {
    display: contents;
  }

  .link {
    display: block;
    text-align: center;
    margin-top: 16px;
    color: #ff4a52;
    text-decoration-line: underline;
  }

  h3 {
    text-align: center;
    font-weight: 600;
  }

  .icon {
    display: inline-block;
    width: 100%;
    text-align: center;
    margin-top: 32px;
    font-size: 50px;
  }

  .immediately {
    color: #888;
    font-size: 14px;
  }
`

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  width: 100%;
  max-width: 480px;
  height: calc(50px + 48px);
  background-color: white;

  color: white;
  text-align: center;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-top: 1px solid #e0e0e0;

  button {
    width: 100%;
    height: 50px;
    background-color: #333;
    border: none;
    color: white;
    border-radius: 5px;
    cursor: pointer;
  }
`
